import { db } from "./db";
import {
  obtenerProductoOffline,
  obtenerPrecioOffline,
  obtenerStockTotalOffline,
} from "./offlineCatalogoService";

function buildFolioLocal() {
  const now = new Date();

  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");

  return `OFF-${yyyy}${mm}${dd}-${hh}${mi}${ss}`;
}

function getId(value) {
  if (value && typeof value === "object") {
    return value.id ?? value.codigo ?? value.almacen ?? "";
  }

  return value ?? "";
}

export function cotizacionTieneIdServidor(cotizacion) {
  if (!cotizacion) return false;

  const idServidor =
    cotizacion.idServidor ??
    cotizacion.idCotizacion ??
    cotizacion.respuestaSync?.id ??
    null;

  return (
    idServidor !== null &&
    idServidor !== undefined &&
    String(idServidor).trim() !== ""
  );
}

async function normalizarCotizacionesRecibidasPorBackend() {
  const cotizaciones = await db.offline_cotizaciones.toArray();
  const fechaNormalizacion = new Date().toISOString();

  const inconsistentes = cotizaciones.filter(
    (item) =>
      cotizacionTieneIdServidor(item) && item.estadoSync !== "SINCRONIZADA"
  );

  if (inconsistentes.length === 0) return;

  await db.transaction("rw", db.offline_cotizaciones, async () => {
    await Promise.all(
      inconsistentes.map((item) =>
        db.offline_cotizaciones.update(item.idLocal, {
          estadoSync: "SINCRONIZADA",
          estadoLocal: item.estadoLocal || "EN_CUARENTENA",
          estadoCuarentena: item.estadoCuarentena || "EN_CUARENTENA",
          fechaActualizacion: fechaNormalizacion,
          fechaSincronizacion: item.fechaSincronizacion || fechaNormalizacion,
          ultimoErrorSync: null,
        })
      )
    );
  });
}

export async function crearCotizacionOffline(data, tipoDocumento) {
  const fechaCreacion = new Date().toISOString();
  const folioLocal = buildFolioLocal();

  const cotizacion = {
    folioLocal,
    tipoDocumento,
    estadoSync: "PENDIENTE",
    fechaCreacion,
    fechaActualizacion: fechaCreacion,

    codigoCliente: data.cliente?.codigo || "",
    cliente: data.cliente || null,

    documento: data.documento,
    contacto: data.contacto || null,
    idContacto: getId(data.contacto),

    estado: data.estado || null,
    idEstado: getId(data.estado),

    validez: data.validez || data.validezs?.[0]?.id || null,
    idValidez: getId(data.validez || data.validezs?.[0]),

    proyecto: data.proyecto || null,
    idProyecto: getId(data.proyecto),

    obra: data.obra || null,
    idObra: getId(data.obra),

    almacen: data.almacen || null,
    idAlmacen:
      data.interfaz === "MAYORISTA"
        ? data.almacen?.almacen
        : data.cliente?.listaPrecio?.almacen,

    listaPrecio: data.cliente?.listaPrecio || null,
    idListaPrecio: data.cliente?.listaPrecio?.codigo || "",

    condicionPago: data.cliente?.condicionPago || null,
    condicionEntrega: data.condicionEntrega || "RETIRO",
    condicion: String(data.condicion || (data.condicionEntrega === "DESPACHO" ? 4 : 3)),
    direccionDespacho: data.direccionDespacho || data?.cliente?.direccion || "",
    referenciaDespacho: data.referenciaDespacho || "",
    comunaDespacho: data.comunaDespacho || data?.cliente?.comuna?.codigo || null,
    valorFlete: Number(data.valorFlete || 0),

    fechaDecision: data.fechaDecision || data.fecha || null,
    fechaEntrega: data.fechaEntrega || data.fecha || null,

    interfaz: data.interfaz || "RETAIL",
    clienteBoleta: data.clienteBoleta || null,
    observaciones: data.observaciones || "",
    descuentoTotal: Number(data.descuentoTotal || 0),

    payloadOriginal: data,
  };

  const idLocal = await db.offline_cotizaciones.add(cotizacion);

  await db.offline_outbox.add({
    tipo: "CREAR_COTIZACION",
    estado: "PENDIENTE",
    fechaCreacion,
    idCotizacionLocal: idLocal,
    folioLocal,
  });

  return {
    idLocal,
    folioLocal,
  };
}

export async function obtenerCotizacionOffline(idLocal) {
  return db.offline_cotizaciones.get(Number(idLocal));
}

export async function listarCotizacionesPendientesOffline() {
  await normalizarCotizacionesRecibidasPorBackend();

  const cotizaciones = await db.offline_cotizaciones.toArray();

  return cotizaciones.filter((item) => {
    if (cotizacionTieneIdServidor(item)) return false;

    return ["PENDIENTE", "ERROR", "ERROR_SYNC"].includes(
      item.estadoSync || "PENDIENTE"
    );
  });
}

function toNumber(value) {
  const number = Number(value);
  return Number.isNaN(number) ? 0 : number;
}

export async function listarDetallesCotizacionOffline(idCotizacionLocal) {
  const detalles = await db.offline_cotizacion_detalles
    .where("idCotizacionLocal")
    .equals(Number(idCotizacionLocal))
    .toArray();

  return detalles.map((detalle) => ({
    ...detalle,
    id: detalle.idLocal,
  }));
}

export async function calcularResumenCotizacionOffline(idCotizacionLocal) {
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);
  const cotizacion = await obtenerCotizacionOffline(idCotizacionLocal);

  const subtotal = detalles.reduce((total, item) => {
    return total + toNumber(item.cantidad) * toNumber(item.precio_lista);
  }, 0);

  const descuento = detalles.reduce((total, item) => {
    const cantidad = toNumber(item.cantidad);
    const precio = toNumber(item.precio);
    const descuentoPorcentaje = toNumber(item.descuento);

    const brutoLinea = cantidad * precio;
    const descuentoLinea = brutoLinea * (descuentoPorcentaje / 100);

    return total + descuentoLinea;
  }, 0);

  const neto = detalles.reduce((total, item) => {
    const cantidad = toNumber(item.cantidad);
    const precio = toNumber(item.precio);
    const descuentoPorcentaje = toNumber(item.descuento);

    const brutoLinea = cantidad * precio;
    const descuentoLinea = brutoLinea * (descuentoPorcentaje / 100);

    return total + brutoLinea - descuentoLinea;
  }, 0);

  const descuentoTotalPorcentaje = Math.min(
    100,
    Math.max(0, toNumber(cotizacion?.descuentoTotal)),
  );
  const descuentoGeneral = neto * (descuentoTotalPorcentaje / 100);
  const netoConDescuento = Math.max(0, neto - descuentoGeneral);
  const iva = Math.round(netoConDescuento * 0.19);
  const total = Math.round(netoConDescuento + iva);

  const flete = toNumber(cotizacion?.valorFlete);

  return {
    subtotal: Math.round(subtotal),
    descuento: Math.round(descuento + descuentoGeneral),
    descuentoTotal: descuentoTotalPorcentaje,
    neto: Math.round(netoConDescuento),
    iva,
    flete: Math.round(flete),
    total: Math.round(total + flete),
  };
}

export async function agregarProductoCotizacionOffline(
  idCotizacionLocal,
  codigoProducto
) {
  const cotizacion = await obtenerCotizacionOffline(idCotizacionLocal);

  if (!cotizacion) {
    throw new Error("Cotización local no encontrada");
  }

  const producto = await obtenerProductoOffline(codigoProducto);

  if (!producto) {
    throw new Error("Producto no encontrado");
  }

  const yaExiste = await db.offline_cotizacion_detalles
    .where("idCotizacionLocal")
    .equals(Number(idCotizacionLocal))
    .and((item) => String(item.codigoProducto) === String(codigoProducto))
    .first();

  if (yaExiste) {
    throw new Error("El producto ya fue agregado");
  }

  const listaPrecio = cotizacion.idListaPrecio;
  const almacen = cotizacion.idAlmacen;

  const precioData = await obtenerPrecioOffline(listaPrecio, codigoProducto);
  const stock = await obtenerStockTotalOffline(almacen, codigoProducto);

  const precio = toNumber(precioData?.precio);
  const fechaCreacion = new Date().toISOString();

  const detalle = {
    idCotizacionLocal: Number(idCotizacionLocal),
    codigoProducto: String(codigoProducto),

    referencia: producto.codigo,
    nombre: producto.descripcion,
    descripcion: producto.descripcion,

    cantidad: 1,
    precio,
    precio_lista: precio,
    descuento: 0,
    total: precio,

    stock,
    unidad: producto.unidad || "",
    um: producto.unidad || "",
    peso: producto.peso || "",

    centro: almacen,
    estadoSync: "PENDIENTE",
    fechaCreacion,
    fechaActualizacion: fechaCreacion,
  };

  const idLocal = await db.offline_cotizacion_detalles.add(detalle);

  await db.offline_cotizacion_detalles.update(idLocal, {
    id: idLocal,
    idLocal,
  });

  await db.offline_cotizaciones.update(Number(idCotizacionLocal), {
    fechaActualizacion: fechaCreacion,
    estadoSync: "PENDIENTE",
  });

  return {
    ...detalle,
    id: idLocal,
    idLocal,
  };
}

function recalcularTotalLinea(linea) {
  const cantidad = Number(linea.cantidad || 0);
  const precio = Number(linea.precio || 0);
  const descuento = Number(linea.descuento || 0);

  const subtotal = cantidad * precio;
  return Math.round(subtotal - subtotal * (descuento / 100));
}

export async function actualizarDetalleCotizacionOffline(
  idDetalleLocal,
  cambios
) {
  const detalle = await db.offline_cotizacion_detalles.get(
    Number(idDetalleLocal)
  );

  if (!detalle) {
    throw new Error("Detalle local no encontrado");
  }

  const actualizado = {
    ...detalle,
    ...cambios,
    fechaActualizacion: new Date().toISOString(),
  };

  actualizado.total = recalcularTotalLinea(actualizado);

  await db.offline_cotizacion_detalles.update(
    Number(idDetalleLocal),
    actualizado
  );

  await db.offline_cotizaciones.update(Number(detalle.idCotizacionLocal), {
    estadoSync: "PENDIENTE",
    fechaActualizacion: new Date().toISOString(),
  });

  return actualizado;
}

export async function eliminarDetalleCotizacionOffline(idDetalleLocal) {
  const detalle = await db.offline_cotizacion_detalles.get(
    Number(idDetalleLocal)
  );

  if (!detalle) {
    throw new Error("Detalle local no encontrado");
  }

  await db.offline_cotizacion_detalles.delete(Number(idDetalleLocal));

  await db.offline_cotizaciones.update(Number(detalle.idCotizacionLocal), {
    estadoSync: "PENDIENTE",
    fechaActualizacion: new Date().toISOString(),
  });
}

export async function actualizarCotizacionOffline(idCotizacionLocal, cambios) {
  const id = Number(idCotizacionLocal);

  if (!id) {
    throw new Error("ID de cotización local inválido");
  }

  const actual = await db.offline_cotizaciones.get(id);

  if (!actual) {
    throw new Error("Cotización local no encontrada");
  }

  const actualizada = {
    ...actual,
    ...cambios,
  };

  let estadoSync;

  if (Object.prototype.hasOwnProperty.call(cambios, "estadoSync")) {
    estadoSync = cambios.estadoSync;
  } else if (cotizacionTieneIdServidor(actualizada)) {
    estadoSync = "SINCRONIZADA";
  } else {
    estadoSync = "PENDIENTE";
  }

  const cambiosPersistir = { ...cambios };

  // Las observaciones pueden cambiar después de crear la cabecera. Se
  // mantienen también dentro de payloadOriginal para que la reconstrucción
  // del formulario y cualquier consumidor legacy lean siempre el mismo valor.
  if (
    Object.prototype.hasOwnProperty.call(cambios, "observaciones") &&
    !Object.prototype.hasOwnProperty.call(cambios, "payloadOriginal")
  ) {
    cambiosPersistir.payloadOriginal = {
      ...(actual.payloadOriginal || {}),
      observaciones: cambios.observaciones ?? "",
    };
  }

  await db.offline_cotizaciones.update(id, {
    ...cambiosPersistir,
    estadoSync,
    fechaActualizacion: new Date().toISOString(),
  });
}

export async function marcarTodosCajaCompletaOffline(
  idCotizacionLocal,
  checked
) {
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);

  await Promise.all(
    detalles.map((detalle) =>
      actualizarDetalleCotizacionOffline(detalle.id, {
        cajaCompleta: checked,
      })
    )
  );
}

export async function actualizarDescuentoTotalCotizacionOffline(
  idCotizacionLocal,
  descuento,
) {
  const valor = Math.min(100, Math.max(0, toNumber(descuento)));
  await actualizarCotizacionOffline(idCotizacionLocal, {
    descuentoTotal: valor,
  });
  return valor;
}

export async function confirmarCotizacionOffline(idCotizacionLocal) {
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);

  if (detalles.length === 0) {
    throw new Error("Debe agregar al menos un producto");
  }

  const cantidadesInvalidas = detalles.some(
    (item) => Number(item.cantidad || 0) <= 0
  );

  if (cantidadesInvalidas) {
    throw new Error("La cantidad de los productos debe ser mayor a 0");
  }

  const total = detalles.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  if (total <= 0) {
    throw new Error("El total no puede ser 0");
  }

  await actualizarCotizacionOffline(idCotizacionLocal, {
    estadoLocal: "CONFIRMADA",
  });

  return true;
}

export async function guardarCotizacionOffline(idCotizacionLocal) {
  await actualizarCotizacionOffline(idCotizacionLocal, {
    estadoLocal: "GUARDADA",
  });

  return true;
}

export async function finalizarCotizacionOffline(idCotizacionLocal) {
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);

  if (detalles.length === 0) {
    throw new Error("Debe agregar al menos un producto");
  }

  const cantidadesInvalidas = detalles.some(
    (item) => Number(item.cantidad || 0) <= 0
  );

  if (cantidadesInvalidas) {
    throw new Error("La cantidad de los productos debe ser mayor a 0");
  }

  const total = detalles.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  if (total <= 0) {
    throw new Error("El total no puede ser 0");
  }

  await actualizarCotizacionOffline(idCotizacionLocal, {
    estadoLocal: "FINALIZADA",
  });

  return true;
}

export async function descartarCotizacionOffline(idCotizacionLocal) {
  const id = Number(idCotizacionLocal);

  await db.offline_cotizacion_detalles
    .where("idCotizacionLocal")
    .equals(id)
    .delete();

  await db.offline_cotizaciones.update(id, {
    estadoSync: "DESCARTADA",
    estadoLocal: "DESCARTADA",
    fechaActualizacion: new Date().toISOString(),
  });

  const outboxItems = await db.offline_outbox.toArray();

  await Promise.all(
    outboxItems
      .filter((item) => Number(item.idCotizacionLocal) === id)
      .map((item) =>
        db.offline_outbox.update(item.idLocal, {
          estado: "DESCARTADA",
        })
      )
  );
}

export async function construirPayloadCrearCotizacionOffline(
  idCotizacionLocal
) {
  const cotizacion = await obtenerCotizacionOffline(idCotizacionLocal);
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);

  if (!cotizacion) {
    throw new Error("Cotización local no encontrada");
  }

  const payload = {
    cliente: String(cotizacion.codigoCliente || ""),
    listaPrecio: String(cotizacion.idListaPrecio || ""),
    idCondicion: Number(cotizacion.condicion || (cotizacion.condicionEntrega === "DESPACHO" ? 4 : 3)),
    observaciones: cotizacion.observaciones || "",
    direccionDespacho: cotizacion.direccionDespacho || "",
    referenciaDespacho: cotizacion.referenciaDespacho || "",
    comunaDespacho: getId(cotizacion.comunaDespacho || cotizacion?.cliente?.comuna) || null,
    valorFlete: Number(cotizacion.valorFlete || 0),
    estadoLocal: cotizacion.estadoLocal || "GUARDADA",
    descuento: Number(cotizacion.descuentoTotal || 0),
    detalles: detalles
      .filter(
        (d) =>
          Number(d.cantidad) > 0 &&
          String(d.codigoProducto || d.referencia || "").trim() !== ""
      )
      .map((detalle) => ({
        codigo: String(detalle.codigoProducto || detalle.referencia),
        valor: Number(detalle.precio || 0),
        descuento: Number(detalle.descuento || 0),
        cantidad: Number(detalle.cantidad || 0),
      })),
  };

  if (payload.detalles.length === 0) {
    throw new Error(
      "La cotización no contiene materiales válidos para sincronizar."
    );
  }

  return payload;
}

export async function marcarCotizacionEnviandoOffline(
  idCotizacionLocal,
  payload
) {
  await actualizarCotizacionOffline(idCotizacionLocal, {
    estadoSync: "ENVIANDO",
    fechaUltimoIntento: new Date().toISOString(),
    payloadSync: payload,
    ultimoErrorSync: null,
  });
}

export async function marcarCotizacionSincronizadaOffline(
  idCotizacionLocal,
  respuesta
) {
  const id = Number(idCotizacionLocal);
  const fechaSincronizacion = new Date().toISOString();
  const idServidor = respuesta?.id ?? respuesta?.idCotizacion ?? null;

  if (
    idServidor === null ||
    idServidor === undefined ||
    String(idServidor).trim() === ""
  ) {
    throw new Error(
      "El backend respondió la creación, pero no entregó el ID de la cotización"
    );
  }

  await db.transaction(
    "rw",
    db.offline_cotizaciones,
    db.offline_outbox,
    async () => {
      await db.offline_cotizaciones.update(id, {
        estadoSync: "SINCRONIZADA",
        // La subida terminó. El estado de negocio sigue pendiente de cuarentena.
        estadoLocal: "EN_CUARENTENA",
        estadoCuarentena: "EN_CUARENTENA",
        fechaSincronizacion,
        fechaActualizacion: fechaSincronizacion,
        idServidor,
        folioServidor: respuesta?.folio || "",
        respuestaSync: respuesta || null,
        ultimoErrorSync: null,
      });

      const outboxItems = (await db.offline_outbox.toArray()).filter(
        (item) => Number(item.idCotizacionLocal) === id
      );

      await Promise.all(
        outboxItems.map((item) =>
          db.offline_outbox.update(item.idLocal, {
            estado: "SINCRONIZADA",
            fechaSincronizacion,
            idServidor,
          })
        )
      );
    }
  );
}

export async function marcarCotizacionErrorSyncOffline(
  idCotizacionLocal,
  error
) {
  const cotizacion = await obtenerCotizacionOffline(idCotizacionLocal);

  // Si ya existe un ID remoto, la cotización fue recibida por el backend.
  // No debe volver a quedar habilitada para envío por un error posterior.
  if (cotizacionTieneIdServidor(cotizacion)) {
    await actualizarCotizacionOffline(idCotizacionLocal, {
      estadoSync: "SINCRONIZADA",
      estadoLocal: cotizacion.estadoLocal || "EN_CUARENTENA",
      estadoCuarentena: cotizacion.estadoCuarentena || "EN_CUARENTENA",
      ultimoErrorSync: null,
    });
    return;
  }

  await actualizarCotizacionOffline(idCotizacionLocal, {
    estadoSync: "ERROR",
    fechaUltimoIntento: new Date().toISOString(),
    ultimoErrorSync: error?.message || String(error),
    respuestaSync: error?.response || null,
  });
}

export async function listarCotizacionesLocalesOffline() {
  return db.offline_cotizaciones.orderBy("fechaCreacion").reverse().toArray();
}
